/* Availability counts are loaded only for visible, nonempty collections.
 * Desktop and drawer copies share a request; session cache expires in 10 min.
 */
(() => {
  const CACHE_TTL = 10 * 60 * 1000;
  const CACHE_PREFIX = 'gg:catalog-count:v1:';
  const MAX_CONCURRENT = 2;
  const jobs = new Map();
  const queue = [];
  let activeRequests = 0;

  function display(badge, count) {
    const formatted = count.toLocaleString('ja-JP');
    badge.textContent = `${formatted}件`;
    badge.dataset.countEmpty = String(count === 0);
    badge.title = `購入可能な商品数：${formatted}件`;
    badge.closest('a').setAttribute('aria-label', `${badge.dataset.countLabel}：購入可能 ${formatted}件`);
  }

  function readCache(job) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(job.cacheKey));
      if (cached && Number.isSafeInteger(cached.count) && cached.count >= 0 &&
          cached.total === job.total && cached.count <= cached.total &&
          cached.expires > Date.now() && cached.expires <= Date.now() + CACHE_TTL) {
        return cached.count;
      }
    } catch (_) {
      // Storage may be disabled. Counts and navigation must still work.
    }
    return null;
  }

  async function loadCount(job) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    try {
      const response = await fetch(job.url, { signal: controller.signal });
      if (!response.ok) throw new Error('Count request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = html.querySelector('[data-catalog-count-result]');
      if (!result || result.dataset.collectionId !== job.collectionId ||
          !/^\d+$/.test(result.dataset.count) || !/^\d+$/.test(result.dataset.total)) {
        throw new Error('Count is unavailable');
      }
      const count = Number(result.dataset.count);
      const total = Number(result.dataset.total);
      if (!Number.isSafeInteger(count) || !Number.isSafeInteger(total) || count > total) {
        throw new Error('Invalid count');
      }
      job.badges.forEach((badge) => display(badge, count));
      try {
        sessionStorage.setItem(job.cacheKey, JSON.stringify({ count, total, expires: Date.now() + CACHE_TTL }));
      } catch (_) {
        // An unavailable cache should not discard a successfully loaded count.
      }
    } catch (_) {
      // Keep the link usable. Unknown is not the same as zero.
      job.badges.forEach((badge) => { badge.title = '購入可能な商品数を取得できませんでした'; });
    } finally {
      clearTimeout(timeout);
      activeRequests -= 1;
      drainQueue();
    }
  }

  function drainQueue() {
    while (activeRequests < MAX_CONCURRENT && queue.length) {
      const job = queue.shift();
      activeRequests += 1;
      loadCount(job);
    }
  }

  document.querySelectorAll('[data-catalog-count]').forEach((badge) => {
    const total = Number(badge.dataset.countTotal);
    if (total === 0) {
      display(badge, 0);
      return;
    }
    const anchor = badge.closest('a');
    const url = new URL(anchor.href, window.location.href);
    // Collection entry links already contain the availability filter. Keep any
    // other filters so the badge matches the actual destination.
    if (url.origin !== window.location.origin || url.searchParams.get('filter.v.availability') !== '1') return;
    url.hash = '';
    ['page', 'sections', 'section_id'].forEach((key) => url.searchParams.delete(key));
    url.searchParams.set('section_id', 'gg-catalog-count');
    url.searchParams.sort();
    const key = `${badge.dataset.countContext}:${url.pathname}${url.search}`;
    if (!jobs.has(key)) {
      jobs.set(key, {
        url: url.href,
        cacheKey: CACHE_PREFIX + key,
        collectionId: badge.dataset.countCollectionId,
        total,
        badges: [],
        queued: false,
      });
    }
    jobs.get(key).badges.push(badge);
  });

  function observeCounts() {
    if (!('IntersectionObserver' in window)) return;
    const jobByBadge = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const job = jobByBadge.get(entry.target);
        if (job.queued) return;
        job.queued = true;
        job.badges.forEach((badge) => observer.unobserve(badge));
        // Check the expiry when the group is opened, not only at page load.
        const cached = readCache(job);
        if (cached !== null) {
          job.badges.forEach((badge) => display(badge, cached));
        } else {
          queue.push(job);
        }
      });
      drainQueue();
    }, { rootMargin: '100px 0px' });
    jobs.forEach((job) => job.badges.forEach((badge) => {
      jobByBadge.set(badge, job);
      observer.observe(badge);
    }));
  }

  // Let the initial page and its main images load before count requests start.
  if (document.readyState === 'complete') observeCounts();
  else window.addEventListener('load', observeCounts, { once: true });
})();
